import { Link } from "react-router-dom";
import { SeoHead } from "@/components/SeoHead";
import { Layout } from "@/components/Layout";
import { MapPin, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";
import { geoByCounty, allGeoTowns } from "@/data/geoPages";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const ServiceArea = () => (
  <Layout>
    <SeoHead
      title="Pocono Mountains Service Area – We're Local, Call Today | Semper Fi Pump Service"
      description="Serving Monroe, Pike, Carbon & Northampton Counties. Local well pump experts within a 60-mile radius of Saylorsburg, PA. 33 years of experience."
    />
    <section className="bg-primary">
      <div className="container py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-extrabold text-primary-foreground mb-4 font-display">
          Well Pump Repair in the Pocono Mountains
        </h1>
        <p className="text-primary-foreground/80 max-w-2xl text-pretty">
          Serving homeowners across a 60-mile radius from Saylorsburg, PA. We know Pocono Mountain wells because we've been repairing them for 33 years.
        </p>
      </div>
    </section>

    <section className="container py-16 md:py-24">
      <h2 className="text-2xl font-extrabold mb-4 font-display">Why Pocono Mountain Wells Are Different</h2>
      <p className="text-muted-foreground text-pretty leading-relaxed mb-6 max-w-3xl">
        The Pocono Mountains sit on unique geological formations that create specific challenges for well water systems. Deep rock wells, variable water tables, and acidic water conditions require a technician who understands the local terrain. Many homes in the region rely on well water systems that were installed decades ago and need experienced hands for maintenance and repair.
      </p>
      <p className="text-muted-foreground text-pretty leading-relaxed mb-12 max-w-3xl">
        Rural properties often have long-run water lines, complex pressure systems, and grinder pump stations that require specialized knowledge. As a lifelong Pocono resident, our owner has personally serviced thousands of systems across the region and understands the nuances of each area.
      </p>

      <h2 className="text-2xl font-extrabold mb-8 font-display">Service Areas by County</h2>
      <motion.div
        className="grid md:grid-cols-2 gap-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={stagger}
      >
        {Object.entries(geoByCounty).map(([county, pages]) => (
          <motion.div key={county} variants={fadeUp} className="bg-card rounded-xl p-6 shadow-card border border-border">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-accent" /> {county}
            </h3>
            <div className="space-y-2">
              {pages.map((page) => (
                <Link
                  key={page.slug}
                  to={`/${page.slug}`}
                  className="flex items-center justify-between text-sm text-muted-foreground hover:text-accent px-3 py-2 rounded-lg hover:bg-accent/5 transition-colors group"
                >
                  <span>Well Pump Repair {page.town}, {page.state}</span>
                  <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* All towns inline for SEO */}
      <div className="mt-16 max-w-3xl">
        <h2 className="text-xl font-extrabold mb-4 font-display">Serving Homeowners Across</h2>
        <p className="text-muted-foreground text-pretty leading-relaxed">
          {allGeoTowns.map((t, i) => (
            <span key={t.slug}>
              <Link to={t.href} className="text-accent hover:underline">{t.name}</Link>
              {i < allGeoTowns.length - 1 ? " · " : ""}
            </span>
          ))}
        </p>
      </div>
    </section>

    <section className="bg-primary">
      <div className="container py-16 text-center">
        <h2 className="text-2xl font-extrabold text-primary-foreground mb-4 font-display">In Our Service Area? Call Now.</h2>
        <p className="text-primary-foreground/80 mb-8">We respond same-day to emergency pump failures throughout the Pocono Mountains.</p>
        <Button asChild variant="emergency" size="xl">
          <a href={PHONE_HREF}><Phone className="w-5 h-5" />Call {PHONE_NUMBER}</a>
        </Button>
      </div>
    </section>
  </Layout>
);

export default ServiceArea;
